import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

/**
 * Last step of the onboarding lifecycle:
 *
 *   Invitation Sent  →  Email Verified  →  Registration Complete
 *
 * The admin has approved the account; the backend has emailed a single-use
 * 72-hour token. This page validates the token (via GET) so it can show
 * "Welcome, {name}", then POSTs the chosen password and bounces the user
 * to the login screen.
 */
export default function CreatePassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/auth/create-password/${token}`);
        if (!cancelled) setUser(r.data);
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || "Activation link is invalid or expired.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/create-password", { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not set password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-sm" data-testid="create-password-page">
        <div className="flex items-center gap-3 mb-10">
          <img src="/rd-logo.jpeg" alt="" className="w-10 h-10 rounded-md object-cover" />
          <span className="font-heading text-2xl font-black tracking-tight">
            RD <span className="text-[#FF3B30]">SmartMetrics</span>
          </span>
        </div>

        {loading && (
          <div className="flex items-center gap-3 text-[#A1A1AA]" data-testid="create-password-loading">
            <Loader2 size={16} className="animate-spin" /> Validating activation link…
          </div>
        )}

        {!loading && err && !user && (
          <div
            className="card-tactical p-5 text-sm border border-[#FF3B30]/40 text-[#FCA5A5]"
            data-testid="create-password-link-error"
          >
            <div className="flex items-start gap-2 mb-3">
              <AlertCircle size={18} className="text-[#FF3B30] mt-0.5" />
              <div className="font-bold uppercase tracking-widest text-[11px] text-[#FF3B30]">
                Link Problem
              </div>
            </div>
            <p>{err}</p>
            <Link to="/login" className="mt-4 inline-block text-xs uppercase tracking-widest underline text-white">
              Back to sign in
            </Link>
          </div>
        )}

        {!loading && user && (
          <>
            <div className="overline mb-2">// final step — activate your account</div>
            <h2 className="font-heading text-4xl font-black uppercase mb-2">Create Password</h2>
            <p className="text-[#A1A1AA] text-sm mb-8">
              Welcome <b className="text-white">{user.name}</b>. Choose a password to finish setting up your{" "}
              <span className="text-white">{user.email}</span> account.
            </p>

            {done ? (
              <div
                className="card-tactical p-5 text-sm text-[#34C759] flex items-center gap-3"
                data-testid="create-password-success"
              >
                <CheckCircle2 size={18} />
                Password set. Redirecting to sign in…
              </div>
            ) : (
              <form onSubmit={submit} data-testid="create-password-form">
                {err && (
                  <div
                    className="mb-4 px-3 py-2 border border-[#FF3B30]/40 text-[#FCA5A5] text-sm"
                    data-testid="create-password-error"
                  >
                    {err}
                  </div>
                )}
                <label className="block mb-5">
                  <span className="overline block mb-2">New Password</span>
                  <input
                    data-testid="create-password-input"
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="w-full bg-transparent border-b border-white/20 py-2 text-white focus:border-[#FF3B30]"
                  />
                </label>
                <label className="block mb-8">
                  <span className="overline block mb-2">Confirm Password</span>
                  <input
                    data-testid="create-password-confirm"
                    type="password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="w-full bg-transparent border-b border-white/20 py-2 text-white focus:border-[#FF3B30]"
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="create-password-submit"
                  className="w-full bg-[#FF3B30] hover:bg-[#D92B22] text-white font-bold uppercase tracking-[0.18em] py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {busy && <Loader2 size={13} className="animate-spin" />}
                  {busy ? "Setting…" : "Activate Account"}
                </button>
              </form>
            )}

            <div className="mt-6 text-xs text-[#A1A1AA]">
              <Link to="/login" className="text-white underline">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
